import * as Y from 'yjs'
import { prosemirrorJSONToYDoc, yDocToProsemirrorJSON } from 'y-prosemirror'
import { generateJSON } from '@tiptap/html'
import {
  type Extensions,
  getSchema,
  generateText,
  JSONContent,
} from '@tiptap/core'
import type { Node } from 'prosemirror-model'
import { isEmpty } from './is-empty'
import { transformationsCache } from './_cache'

export const plainTextToYDoc = (plainText: string, extensions: Extensions) => {
  const json = generateJSON(plainText, extensions) as Node
  const schema = getSchema(extensions)
  const ydoc = prosemirrorJSONToYDoc(schema, json, 'content')
  return { ydoc, json, schema }
}

export const plainTextToYDocBase64State = (
  plainText: string,
  extensions: Extensions
) => {
  const { ydoc, json, schema } = plainTextToYDoc(plainText, extensions)
  const update = Y.encodeStateAsUpdate(ydoc)
  const base64 = Buffer.from(update).toString('base64')
  return { base64, ydoc, json, schema }
}

export const jsonToYDoc = (json: JSONContent, extensions: Extensions) => {
  const schema = getSchema(extensions)
  const ydoc = prosemirrorJSONToYDoc(schema, json, 'content')
  return { ydoc, json, schema }
}

export const jsonToYDocBase64State = (
  json: JSONContent,
  extensions: Extensions
) => {
  const { ydoc, schema } = jsonToYDoc(json, extensions)
  const update = Y.encodeStateAsUpdate(ydoc)
  const base64 = Buffer.from(update).toString('base64')
  return { base64, ydoc, json, schema }
}

export const yDocToPlainText = (yDoc: Y.Doc, extensions: Extensions) => {
  const prosemirrorJson = yDocToProsemirrorJSON(yDoc, 'content')
  const text = generateText(prosemirrorJson, extensions)

  return text
}

export const jsonToPlainText = (
  json: Record<string, unknown>,
  extensions: Extensions
) => {
  const text = generateText(json, extensions)
  return text
}

export const yDocToJson = (yDoc: Y.Doc) => {
  const prosemirrorJson = yDocToProsemirrorJSON(yDoc, 'content') as JSONContent
  return prosemirrorJson
}

export const stateToJson = (
  state: string,
  opts?: { cacheKey?: string; debug?: boolean }
): JSONContent => {
  if (opts?.debug) {
    console.log('stateToJson', state)
  }

  if (opts?.cacheKey) {
    const cached = transformationsCache.get('state-to-json' + opts.cacheKey)
    if (cached) {
      return cached as JSONContent
    }
  }

  /**
   * commenting this auto-cache for now because it introduced a bug (i think)
   */

  // const cached = transformationsCache.get('state-to-json' + state)
  // if (cached) {
  //   if (opts?.debug) {
  //     console.log('cached', JSON.stringify(cached))
  //   }
  //   return cached as JSONContent
  // }
  const result = normalizeJsonState(
    isJSONFormat(state)
      ? (JSON.parse(state) as any)
      : yDocToJson(buildYDocFromState(state))
  )
  if (opts?.cacheKey) {
    transformationsCache.set('state-to-json' + opts.cacheKey, result)
  }
  // transformationsCache.set('state-to-json' + state, result)
  if (opts?.debug) {
    console.log('result', JSON.stringify(result))
  }
  return result
}

export const stateToBase64 = (
  state: string,
  extensions: Extensions
): string => {
  try {
    // block is commited
    const parsed = JSON.parse(state) as any
    return jsonToYDocBase64State(parsed, extensions).base64
  } catch (error) {
    return state
  }
}

export const buildYDocFromState = (state: string, options?: any) => {
  const ydoc = new Y.Doc(options)

  if (state) {
    // convert string to unit8array:
    const unit8array = Buffer.from(state, 'base64')
    // apply update to ydoc
    Y.applyUpdate(ydoc, unit8array)
  }

  return ydoc
}

export const emptyTipTapContent = {
  type: 'doc',
  content: [
    {
      type: 'paragraph',
      content: [],
    },
  ],
}

/**
 * @name normalizeJsonState
 * @param {JSONContent} state
 * @description Normalize text in JSONContent format to be used in Yjs
 * @example normalizeJsonState(richText)
 * @returns {JSONContent}
 */
export const normalizeJsonState = (state: JSONContent) => {
  if (!state || isEmpty(state)) {
    return emptyTipTapContent
  }

  return mapJSONNode(state, (node): JSONContent => {
    if (node.type === 'text') {
      if (!node.text) {
        return []
      }
      return {
        ...node,
        /** Remove non-breaking spaces as browsers add these after pasting sometimes (https://discuss.prosemirror.net/t/non-breaking-spaces-being-added-to-pasted-html/3911/5) */
        text: node.text.replace(/\u00A0/g, ' '),
      }
    }
    return node
  })
}

/**
 * @description Check if a string is in JSON format
 * @example isJSONFormat(richText)
 * @returns {boolean}
 * @example isJSONFormat('{"type":"doc","content":[{"type":"paragraph","content":[{"type":"text","text":"Hello world"}]}]}') // true
 * @example isJSONFormat('Hello world') // false
 */

export const isJSONFormat = (state: string) => {
  try {
    JSON.parse(state)
    return true
  } catch (error) {
    return false
  }
}

/**
 * @name mapJSONNode
 * @description Map a JSONContent node to mutate with a function
 *
 */

export const mapJSONNode = (
  node: JSONContent,
  fn: (node: JSONContent) => JSONContent
): JSONContent => {
  if (!node.content) {
    return fn(node)
  } else if (node.content.length === 0) {
    const newNode = fn(node)
    delete newNode.content
    return newNode
  }
  return {
    ...node,
    content: node.content
      .flatMap((child) => mapJSONNode(child, fn))
      .filter(Boolean),
  }
}
